import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CATEGORIES, ARTICLES, articlesByCategory } from "./helpData";
import {
  Search, Rocket, CalendarDays, TrendingUp, Settings, ArrowRight, FileText, BookOpen,
} from "lucide-react";

const ICONS = { Rocket, CalendarDays, TrendingUp, Settings };

const HelpHeader = () => (
  <header className="border-b border-border sticky top-0 z-30 bg-background/90 backdrop-blur">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
      <Link to="/" className="flex items-center gap-2.5" data-testid="help-home-link">
        <img src="/azordi-logo.webp" alt="azordi" className="h-7 w-auto" />
      </Link>
      <nav className="flex items-center gap-5 text-sm">
        <Link to="/help" className="text-foreground font-medium" data-testid="help-nav-help">Help Centre</Link>
        <Link to="/login" className="text-muted-foreground hover:text-foreground" data-testid="help-nav-login">Sign in</Link>
        <Link to="/register" className="text-muted-foreground hover:text-foreground" data-testid="help-nav-register">Get started</Link>
      </nav>
    </div>
  </header>
);

const ArticleRow = ({ a }) => (
  <Link
    to={`/help/${a.slug}`}
    data-testid={`help-article-link-${a.slug}`}
    className="group flex items-start gap-3 p-4 rounded-xl border border-border hover:border-primary/50 hover:bg-muted/50 transition-colors"
  >
    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
      <FileText className="h-4 w-4 text-primary" />
    </div>
    <div className="min-w-0">
      <div className="font-medium text-sm group-hover:text-primary transition-colors">{a.title}</div>
      <div className="text-sm text-muted-foreground line-clamp-2 mt-0.5">{a.summary}</div>
      <div className="text-[11px] text-muted-foreground mt-1.5">{a.readMins} min read</div>
    </div>
    <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1 shrink-0" />
  </Link>
);

export default function HelpCenter() {
  const [q, setQ] = useState("");

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return null;
    return ARTICLES.filter((a) =>
      [a.title, a.summary, a.intro, ...(a.keywords || [])].join(" ").toLowerCase().includes(term)
    );
  }, [q]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="help-center-page">
      <HelpHeader />

      {/* Hero + search */}
      <section className="border-b border-border bg-gradient-to-b from-primary/5 to-transparent">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-mono-label uppercase tracking-widest text-primary mb-4">
            <BookOpen className="h-4 w-4" /> Azordi Help Centre
          </div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tight">How can we help?</h1>
          <p className="text-muted-foreground mt-4 text-base sm:text-lg">Step-by-step guides for every part of Azordi — with real screenshots.</p>
          <div className="relative mt-8 max-w-xl mx-auto">
            <Search className="h-5 w-5 text-muted-foreground absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search guides… (e.g. bookings, SMS, reports)"
              data-testid="help-search-input"
              className="w-full h-14 pl-12 pr-4 rounded-2xl border border-border bg-card text-base shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
            />
          </div>
        </div>
      </section>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {results ? (
          <div data-testid="help-search-results">
            <p className="text-sm text-muted-foreground mb-5">
              {results.length} {results.length === 1 ? "result" : "results"} for “{q}”
            </p>
            {results.length ? (
              <div className="grid sm:grid-cols-2 gap-3">
                {results.map((a) => <ArticleRow key={a.slug} a={a} />)}
              </div>
            ) : (
              <p className="text-muted-foreground">No guides matched. Try a different word, or browse the categories below.</p>
            )}
          </div>
        ) : (
          <div className="space-y-14">
            {CATEGORIES.map((cat) => {
              const Icon = ICONS[cat.icon] || FileText;
              const arts = articlesByCategory(cat.slug);
              return (
                <section key={cat.slug} data-testid={`help-category-${cat.slug}`}>
                  <div className="flex items-center gap-3 mb-5">
                    <div className="h-11 w-11 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h2 className="font-heading font-bold text-xl">{cat.title}</h2>
                      <p className="text-sm text-muted-foreground">{cat.desc}</p>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {arts.map((a) => <ArticleRow key={a.slug} a={a} />)}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </main>

      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-muted-foreground">
          <span>Still stuck? The AI Assistant inside Azordi can answer most questions instantly.</span>
          <div className="flex gap-5">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <Link to="/terms" className="hover:text-foreground">Terms</Link>
            <Link to="/privacy" className="hover:text-foreground">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
